import React, { useState, useEffect } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import * as Select from '@radix-ui/react-select';
import { Search, Filter, MessageSquare, Clock, CheckCircle, AlertCircle, X, Mail, Phone, ChevronDown, Save } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { format } from 'date-fns';

interface Filters {
  status: string;
  priority: string;
  searchQuery: string;
}

const TICKET_STATUSES = ['Open', 'In Progress', 'Resolved'] as const;
const TICKET_PRIORITIES = ['High', 'Medium', 'Low'] as const;

export function CustomerSupport() {
  const { supportTickets, markTicketAsRead, updateTicket, addInternalNote } = useNotifications();
  const [selectedTicket, setSelectedTicket] = useState<typeof supportTickets[0] | null>(null);
  const [filters, setFilters] = useState<Filters>({
    status: 'All',
    priority: 'All',
    searchQuery: ''
  });
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [newNote, setNewNote] = useState('');

  // Auto-refresh tickets every second
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleStatusChange = (ticketId: string, newStatus: typeof TICKET_STATUSES[number]) => {
    const ticket = supportTickets.find(t => t.id === ticketId);
    if (ticket) {
      const updatedTicket = {
        ...ticket,
        status: newStatus,
        lastUpdated: new Date().toISOString()
      };
      updateTicket(updatedTicket);
      
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(updatedTicket);
      }
    }
  };

  const handlePriorityChange = (ticketId: string, newPriority: typeof TICKET_PRIORITIES[number]) => {
    const ticket = supportTickets.find(t => t.id === ticketId);
    if (ticket) {
      const updatedTicket = {
        ...ticket,
        priority: newPriority,
        lastUpdated: new Date().toISOString()
      };
      updateTicket(updatedTicket);
      
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(updatedTicket);
      }
    }
  };

  const handleAddNote = () => {
    if (selectedTicket && newNote.trim()) {
      addInternalNote(selectedTicket.id, newNote.trim());
      setNewNote('');
      
      // Update selected ticket to show new note immediately
      const updatedTicket = supportTickets.find(t => t.id === selectedTicket.id);
      if (updatedTicket) {
        setSelectedTicket(updatedTicket);
      }
    }
  };

  const filteredTickets = supportTickets
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .filter(ticket => {
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        return (
          ticket.customer.name.toLowerCase().includes(searchLower) ||
          ticket.customer.email.toLowerCase().includes(searchLower) ||
          ticket.customer.description.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters.status !== 'All' && ticket.status !== filters.status) {
        return false;
      }
      
      if (filters.priority !== 'All' && ticket.priority !== filters.priority) {
        return false;
      }
      
      return true;
    });

  return (
    <div className="h-[calc(100vh-theme(spacing.32))]">
      <Tabs.Root
        defaultValue="tickets"
        className="flex h-full"
      >
        <div className="w-1/3 border-r border-gray-200 h-full flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Support Tickets</h2>
              <button
                onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                className="p-2 text-gray-400 hover:text-gray-500"
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filters Panel */}
            {isFilterMenuOpen && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="w-full rounded-md border-gray-300"
                    >
                      <option value="All">All</option>
                      {TICKET_STATUSES.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={filters.priority}
                      onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                      className="w-full rounded-md border-gray-300"
                    >
                      <option value="All">All</option>
                      {TICKET_PRIORITIES.map(priority => (
                        <option key={priority} value={priority}>{priority}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tickets List */}
          <div className="flex-1 overflow-y-auto">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => {
                  setSelectedTicket(ticket);
                  if (!ticket.isRead) {
                    markTicketAsRead(ticket.id);
                  }
                }}
                className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                  selectedTicket?.id === ticket.id ? 'bg-blue-50' : ''
                } ${!ticket.isRead ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {ticket.customer.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {ticket.customer.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    {/* Status Dropdown */}
                    <Select.Root
                      value={ticket.status}
                      onValueChange={(value) => handleStatusChange(ticket.id, value as typeof TICKET_STATUSES[number])}
                    >
                      <Select.Trigger
                        className={`inline-flex items-center justify-between px-2 py-1 text-xs font-medium rounded-full min-w-[100px] ${
                          ticket.status === 'Open' ? 'bg-yellow-100 text-yellow-800' :
                          ticket.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select.Value />
                        <Select.Icon>
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Select.Icon>
                      </Select.Trigger>

                      <Select.Portal>
                        <Select.Content
                          className="bg-white rounded-lg shadow-lg border border-gray-200 min-w-[100px]"
                          position="popper"
                          sideOffset={5}
                        >
                          <Select.Viewport className="p-1">
                            {TICKET_STATUSES.map((status) => (
                              <Select.Item
                                key={status}
                                value={status}
                                className={`relative flex items-center px-2 py-1.5 text-xs rounded-md cursor-default select-none outline-none ${
                                  ticket.status === status
                                    ? 'bg-blue-50 text-blue-900'
                                    : 'text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                <Select.ItemText>{status}</Select.ItemText>
                                <Select.ItemIndicator className="absolute left-2 flex items-center justify-center">
                                  <CheckCircle className="w-3 h-3" />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>

                    {/* Priority Dropdown */}
                    <Select.Root
                      value={ticket.priority}
                      onValueChange={(value) => handlePriorityChange(ticket.id, value as typeof TICKET_PRIORITIES[number])}
                    >
                      <Select.Trigger
                        className={`inline-flex items-center justify-between px-2 py-1 text-xs font-medium rounded-full min-w-[100px] ${
                          ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                          ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select.Value />
                        <Select.Icon>
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Select.Icon>
                      </Select.Trigger>

                      <Select.Portal>
                        <Select.Content
                          className="bg-white rounded-lg shadow-lg border border-gray-200 min-w-[100px]"
                          position="popper"
                          sideOffset={5}
                        >
                          <Select.Viewport className="p-1">
                            {TICKET_PRIORITIES.map((priority) => (
                              <Select.Item
                                key={priority}
                                value={priority}
                                className={`relative flex items-center px-2 py-1.5 text-xs rounded-md cursor-default select-none outline-none ${
                                  ticket.priority === priority
                                    ? 'bg-blue-50 text-blue-900'
                                    : 'text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                <Select.ItemText>{priority}</Select.ItemText>
                                <Select.ItemIndicator className="absolute left-2 flex items-center justify-center">
                                  <CheckCircle className="w-3 h-3" />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                </div>
                <div className="mt-2 flex items-center text-xs text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  {format(new Date(ticket.createdAt), 'MMM d, h:mm a')}
                  {!ticket.isRead && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      New
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ticket Details */}
        <div className="flex-1 flex flex-col h-full">
          {selectedTicket ? (
            <>
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      Support Ticket #{selectedTicket.id}
                    </h2>
                    <div className="flex items-center space-x-4">
                      {/* Status Dropdown in Details View */}
                      <Select.Root
                        value={selectedTicket.status}
                        onValueChange={(value) => handleStatusChange(selectedTicket.id, value as typeof TICKET_STATUSES[number])}
                      >
                        <Select.Trigger
                          className={`inline-flex items-center justify-between px-3 py-1.5 text-sm font-medium rounded-full min-w-[120px] ${
                            selectedTicket.status === 'Open' ? 'bg-yellow-100 text-yellow-800' :
                            selectedTicket.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}
                        >
                          <Select.Value />
                          <Select.Icon>
                            <ChevronDown className="w-4 h-4 ml-1" />
                          </Select.Icon>
                        </Select.Trigger>

                        <Select.Portal>
                          <Select.Content
                            className="bg-white rounded-lg shadow-lg border border-gray-200"
                            position="popper"
                            sideOffset={5}
                          >
                            <Select.Viewport className="p-1">
                              {TICKET_STATUSES.map((status) => (
                                <Select.Item
                                  key={status}
                                  value={status}
                                  className={`relative flex items-center px-3 py-2 text-sm rounded-md cursor-default select-none outline-none ${
                                    selectedTicket.status === status
                                      ? 'bg-blue-50 text-blue-900'
                                      : 'text-gray-900 hover:bg-gray-50'
                                  }`}
                                >
                                  <Select.ItemText>{status}</Select.ItemText>
                                  <Select.ItemIndicator className="absolute left-2 flex items-center justify-center">
                                    <CheckCircle className="w-4 h-4" />
                                  </Select.ItemIndicator>
                                </Select.Item>
                              ))}
                            </Select.Viewport>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>

                      {/* Priority Dropdown in Details View */}
                      <Select.Root
                        value={selectedTicket.priority}
                        onValueChange={(value) => handlePriorityChange(selectedTicket.id, value as typeof TICKET_PRIORITIES[number])}
                      >
                        <Select.Trigger
                          className={`inline-flex items-center justify-between px-3 py-1.5 text-sm font-medium rounded-full min-w-[120px] ${
                            selectedTicket.priority === 'High' ? 'bg-red-100 text-red-800' :
                            selectedTicket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}
                        >
                          <Select.Value />
                          <Select.Icon>
                            <ChevronDown className="w-4 h-4 ml-1" />
                          </Select.Icon>
                        </Select.Trigger>

                        <Select.Portal>
                          <Select.Content
                            className="bg-white rounded-lg shadow-lg border border-gray-200"
                            position="popper"
                            sideOffset={5}
                          >
                            <Select.Viewport className="p-1">
                              {TICKET_PRIORITIES.map((priority) => (
                                <Select.Item
                                  key={priority}
                                  value={priority}
                                  className={`relative flex items-center px-3 py-2 text-sm rounded-md cursor-default select-none outline-none ${
                                    selectedTicket.priority === priority
                                      ? 'bg-blue-50 text-blue-900'
                                      : 'text-gray-900 hover:bg-gray-50'
                                  }`}
                                >
                                  <Select.ItemText>{priority}</Select.ItemText>
                                  <Select.ItemIndicator className="absolute left-2 flex items-center justify-center">
                                    <CheckCircle className="w-4 h-4" />
                                  </Select.ItemIndicator>
                                </Select.Item>
                              ))}
                            </Select.Viewport>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Customer Information */}
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      <a 
                        href={`mailto:${selectedTicket.customer.email}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {selectedTicket.customer.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <a 
                        href={`tel:${selectedTicket.customer.phone}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {selectedTicket.customer.phone}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Customer Issue */}
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Issue Description</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedTicket.customer.description}
                  </p>
                </div>
              </div>

              {/* Messages and Internal Notes */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                  {/* Messages Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-900">Messages</h3>
                    {selectedTicket.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.type === 'customer' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div className={`max-w-lg rounded-lg p-4 ${
                          message.type === 'customer'
                            ? 'bg-gray-100'
                            : message.type === 'bot'
                            ? 'bg-blue-50'
                            : 'bg-blue-600 text-white'
                        }`}>
                          <p className="text-sm">{message.content}</p>
                          <p className="text-xs mt-1 opacity-70">
                            {format(new Date(message.timestamp), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Internal Notes Section */}
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-yellow-900 mb-4">Internal Notes</h3>
                    
                    {/* Existing Notes */}
                    <div className="space-y-4 mb-4">
                      {selectedTicket.internalNotes?.map((note) => (
                        <div key={note.id} className="bg-white rounded-lg p-4 shadow-sm">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                            <span>{note.createdBy}</span>
                            <span>{format(new Date(note.createdAt), 'MMM d, h:mm a')}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add New Note */}
                    <div className="space-y-2">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add an internal note..."
                        className="w-full p-3 border border-yellow-200 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                        rows={3}
                      />
                      <button
                        onClick={handleAddNote}
                        disabled={!newNote.trim()}
                        className="flex items-center justify-center w-full px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-yellow-300 disabled:cursor-not-allowed transition-colors"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Note
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No ticket selected</h3>
                <p className="text-gray-500">Select a ticket from the list to view details</p>
              </div>
            </div>
          )}
        </div>
      </Tabs.Root>
    </div>
  );
}